// The share sheet for a ride graphic: pick the ground, the size and the name, then share it
// straight to Instagram (or anywhere) through the phone's share sheet, or save the image.
import { useEffect, useRef, useState } from "preact/hooks";
import { Sheet } from "./Sheet";
import { BGS, drawGraphic, fontsReady, graphicFile, heightOf, loadImage, W, type BgId, type Size } from "../lib/graphic";
import { proxied, type P } from "../lib/tiles";
import type { Ride } from "../lib/types";
import { haptic } from "../lib/haptic";
import { bikes } from "../lib/store";

const PARIS = "2027-06-12";
const daysTo = (from: string) => Math.round((Date.parse(PARIS) - Date.parse(from)) / 864e5);
const clock = (s: number) => { const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = Math.round(s % 60); return `${h}:${String(m).padStart(2, "0")}:${String(x).padStart(2, "0")}`; };
const km = (a: P, b: { lat: number; lon: number }) => { const R = 6371, dLa = ((b.lat - a[0]) * Math.PI) / 180, dLo = ((b.lon - a[1]) * Math.PI) / 180; const h = Math.sin(dLa / 2) ** 2 + Math.cos((a[0] * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLo / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };

export function RideGraphic({ open, onClose, r, pts, home, profile }: { open: boolean; onClose: () => void; r: Ride; pts: P[]; home?: { lat: number; lon: number }; profile?: [number, number][] }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const pick = useRef<HTMLInputElement>(null);
  const [size, setSize] = useState<Size>("story");
  const [bg, setBg] = useState<BgId>(r.photo ? "ride" : "map");
  const [title, setTitle] = useState(r.name ?? "Ride");
  const [paris, setParis] = useState(true);
  const [own, setOwn] = useState<HTMLImageElement | ImageBitmap | null>(null);
  const [ridePhoto, setRidePhoto] = useState<HTMLImageElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [bike, setBike] = useState<string | null>(null);
  const [showBike, setShowBike] = useState(true);
  const [showHeight, setShowHeight] = useState(true);
  useEffect(() => { if (open && r.g && bike == null) bikes().then((b) => setBike(b.bikes.find((x) => x.id === r.g)?.name ?? "")).catch(() => setBike("")); }, [open, r.g]);
  const [note, setNote] = useState("");

  useEffect(() => { if (open && r.photo && !ridePhoto) loadImage(proxied(r.photo)).then(setRidePhoto).catch(() => { setRidePhoto(null); if (bg === "ride") setBg("map"); }); }, [open, r.photo]);

  const day = new Date(`${r.d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const place = ""; // the place comes from the ride's own start once places are named; never a fixed town
  const kmh = r.s > 0 ? (r.km / (r.s / 3600)).toFixed(1) : "–";
  const left = daysTo(r.d);
  const spec = {
    size, bg, title, sub: `${day}${place}`, pts,
    photo: bg === "own" ? own : bg === "ride" ? ridePhoto : null,
    stats: [
      { label: "Distance", value: (Math.round(r.km * 10) / 10).toFixed(1), unit: "km" },
      { label: "Moving time", value: r.s > 0 ? clock(r.s) : "–" },
      { label: "Climbing", value: r.m.toLocaleString("en-GB"), unit: "m" },
      { label: "Average", value: kmh, unit: "km/h" },
    ],
    foot: paris && left > 0 ? `Paris in ${left} days` : null,
    left: showBike && bike ? bike : null,
    profile: showHeight && profile?.length ? profile : null,
  } as const;

  useEffect(() => {
    if (!open || !canvas.current || pts.length < 2) return;
    let live = true;
    (async () => { await fontsReady(); if (live && canvas.current) await drawGraphic(canvas.current, spec as any); })();
    return () => { live = false; };
  }, [open, size, bg, title, paris, own, ridePhoto, bike, showBike, showHeight]);

  const choose = (id: BgId) => { if (id === "own") { pick.current?.click(); return; } setBg(id); haptic("tick"); };
  const onFile = async (f?: File) => {
    if (!f) return;
    try { const img = "createImageBitmap" in window ? await createImageBitmap(f) : await loadImage(URL.createObjectURL(f)); setOwn(img); setBg("own"); } catch { setNote("That photo would not open. Try another."); }
  };
  const name = `musette-${r.d}`;
  const share = async () => {
    if (!canvas.current) return;
    setBusy(true); setNote("");
    try {
      const file = await graphicFile(canvas.current, name);
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file] }); haptic("thud"); }
      else save(file);
    } catch (e: any) { if (e?.name !== "AbortError") setNote("Couldn't share it. Save the image instead."); }
    setBusy(false);
  };
  const save = async (f?: File) => {
    if (!canvas.current) return;
    const file = f ?? await graphicFile(canvas.current, name);
    const a = document.createElement("a"); a.href = URL.createObjectURL(file); a.download = file.name; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const opts = BGS.filter((b) => b.id !== "ride" || (r.photo && ridePhoto));
  return (
    <Sheet open={open} onClose={onClose} label="Share this ride">
      <div class="stack graphic-sheet">
        <div class="row-between"><span class="label ink">Share this ride</span><span class="label">{size === "story" ? "Story · 1080 × 1920" : "Post · 1080 × 1350"}</span></div>
        <div class="graphic-frame" style={{ aspectRatio: `${W} / ${heightOf(size)}` }}>
          <canvas ref={canvas} aria-label={`The graphic: ${title}, ${r.km} km`} />
        </div>
        <div class="chips" role="radiogroup" aria-label="Background">
          {opts.map((b) => <button type="button" role="radio" aria-checked={bg === b.id} class={`chip${bg === b.id ? " on" : ""}`} onClick={() => choose(b.id)}>{b.label}</button>)}
        </div>
        <input ref={pick} type="file" accept="image/*" hidden onChange={(e) => onFile((e.target as HTMLInputElement).files?.[0])} />
        <div class="chips" role="group" aria-label="Size and extras">
          <button type="button" role="radio" aria-checked={size === "story"} class={`chip${size === "story" ? " on" : ""}`} onClick={() => setSize("story")}>Story</button>
          <button type="button" role="radio" aria-checked={size === "post"} class={`chip${size === "post" ? " on" : ""}`} onClick={() => setSize("post")}>Post</button>
          {profile && profile.length > 1 && <button type="button" aria-pressed={showHeight} class={`chip${showHeight ? " on" : ""}`} onClick={() => setShowHeight(!showHeight)}>Height</button>}
          {bike && <button type="button" aria-pressed={showBike} class={`chip${showBike ? " on" : ""}`} onClick={() => setShowBike(!showBike)}>Bike</button>}
          {left > 0 && <button type="button" aria-pressed={paris} class={`chip${paris ? " on" : ""}`} onClick={() => setParis(!paris)}>Paris countdown</button>}
        </div>
        <div class="composer"><input value={title} maxLength={40} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} aria-label="Name on the graphic" /></div>
        <button type="button" class="pill wide" disabled={busy} onClick={share}>{busy ? "Making the image…" : "Share"}</button>
        <button type="button" class="text-btn quiet" onClick={() => save()}>Save the image</button>
        {note && <span class="soft">{note}</span>}
        <span class="label">Distance, time, climbing and speed measured by Strava. Map © OpenStreetMap © CARTO.</span>
      </div>
    </Sheet>
  );
}
